/**
 * Sample supplier AI register — seed data for `/samples/supplier-ai-register`.
 *
 * Public marketing surface. All company names, executives, and identifiers
 * are fictional. Chosen to illustrate the disposition taxonomy + the
 * undisclosed-AI signal that is Motion 2's differentiator.
 *
 * Lifecycle vocabulary per docs/v4/positioning.md — no "exposure" label
 * anywhere (retired product-wide, Elena's Caremark Blocker).
 */

export type SupplierDisposition =
  | 'under_systemic_oversight'
  | 'pending_review'
  | 'awaiting_classification'
  | 'outside_oversight_scope'
  | 'retired';

export interface SupplierSignal {
  kind:
    | 'sig_2026_ai_attestation'
    | 'soc2_ai_supplement'
    | 'iso42001_certificate'
    | 'identity_log_correlation'
    | 'contract_ai_addendum'
    | 'discovered_undisclosed';
  label: string;
  observed: string; // ISO date
}

export interface SupplierRow {
  id: string;
  name: string;
  category: string;
  disposition: SupplierDisposition;
  lastAttested: string | null; // ISO date, or null if never
  nextAttestationDue: string | null;
  riskTier: 'high' | 'medium' | 'low';
  aiSystemsCount: number; // how many AI systems this vendor uses on your data
  frameworkCoveragePct: number; // % of your required framework controls the vendor's attestation covers
  score: number; // 0-100 vs your risk bar
  signals: SupplierSignal[];
  namedSigner: { displayName: string; role: string; company: string } | null;
  narrative: string;
  undisclosedAi?: boolean; // flagged from identity-log correlation
}

const T = (offsetDays: number): string => {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString();
};

export const SUPPLIER_ROWS: SupplierRow[] = [
  {
    id: 'sup-001',
    name: 'Northwind Analytics',
    category: 'Data warehouse + BI',
    disposition: 'under_systemic_oversight',
    lastAttested: T(-22),
    nextAttestationDue: T(68),
    riskTier: 'medium',
    aiSystemsCount: 3,
    frameworkCoveragePct: 94,
    score: 88,
    signals: [
      { kind: 'sig_2026_ai_attestation', label: 'SIG 2026 AI section — complete', observed: T(-22) },
      { kind: 'soc2_ai_supplement', label: 'SOC 2 + AI supplement', observed: T(-40) },
      { kind: 'iso42001_certificate', label: 'ISO 42001 certificate — current', observed: T(-120) },
    ],
    namedSigner: { displayName: 'Priya Ramanathan', role: 'VP Legal', company: 'Northwind Analytics' },
    narrative: 'Attests three AI systems in-tenant (query summarization, anomaly detection, forecast). Data-boundary declaration: US-only, no training on customer data.',
  },
  {
    id: 'sup-002',
    name: 'Kestrel Identity',
    category: 'SSO + directory',
    disposition: 'under_systemic_oversight',
    lastAttested: T(-11),
    nextAttestationDue: T(79),
    riskTier: 'high',
    aiSystemsCount: 2,
    frameworkCoveragePct: 91,
    score: 82,
    signals: [
      { kind: 'sig_2026_ai_attestation', label: 'SIG 2026 AI section — complete', observed: T(-11) },
      { kind: 'soc2_ai_supplement', label: 'SOC 2 + AI supplement', observed: T(-70) },
    ],
    namedSigner: { displayName: 'Marcus Chen', role: 'Head of Eng', company: 'Kestrel Identity' },
    narrative: 'Two AI features: adaptive access risk scoring and login anomaly detection. Both flagged as high risk due to access-decision authority.',
  },
  {
    id: 'sup-003',
    name: 'Peregrine Ops',
    category: 'DevOps + observability',
    disposition: 'pending_review',
    lastAttested: T(-95),
    nextAttestationDue: T(-5),
    riskTier: 'medium',
    aiSystemsCount: 4,
    frameworkCoveragePct: 68,
    score: 64,
    signals: [
      { kind: 'sig_2026_ai_attestation', label: 'SIG 2025 AI section (older)', observed: T(-95) },
      { kind: 'contract_ai_addendum', label: 'Contract AI addendum on file', observed: T(-160) },
    ],
    namedSigner: { displayName: 'Jordan Whitfield', role: 'Chief Compliance Officer', company: 'Peregrine Ops' },
    narrative: 'Attestation is 5 days past due. Auto-refresh request sent; awaiting response. Flagged for TPRM review.',
  },
  {
    id: 'sup-004',
    name: 'Meridian Payroll',
    category: 'HR + payroll SaaS',
    disposition: 'under_systemic_oversight',
    lastAttested: T(-33),
    nextAttestationDue: T(57),
    riskTier: 'high',
    aiSystemsCount: 1,
    frameworkCoveragePct: 88,
    score: 79,
    signals: [
      { kind: 'sig_2026_ai_attestation', label: 'SIG 2026 AI section — complete', observed: T(-33) },
      { kind: 'contract_ai_addendum', label: 'Contract AI addendum on file', observed: T(-220) },
    ],
    namedSigner: { displayName: 'Elena Vartan', role: 'General Counsel', company: 'Meridian Payroll' },
    narrative: 'One AI system: resume screening (HR-only, no candidate-facing decisions). NYC AEDT Local Law 144 bias audit filed.',
  },
  {
    id: 'sup-005',
    name: 'Argus Support Cloud',
    category: 'Customer support platform',
    disposition: 'outside_oversight_scope',
    lastAttested: null,
    nextAttestationDue: T(-14),
    riskTier: 'high',
    aiSystemsCount: 3,
    frameworkCoveragePct: 0,
    score: 22,
    signals: [
      { kind: 'discovered_undisclosed', label: 'Identity-log correlation: 47 Argus AI Assistant sessions/week', observed: T(-2) },
      { kind: 'identity_log_correlation', label: 'Data-boundary unknown', observed: T(-2) },
    ],
    namedSigner: null,
    narrative: 'No AI attestation on file. Our Okta logs show 47 weekly sessions using the Argus AI Assistant feature. Vendor has not responded to attestation request. Remediation SLA active — 14 days remaining before board escalation.',
    undisclosedAi: true,
  },
  {
    id: 'sup-006',
    name: 'HaloDoc Health',
    category: 'PHI processing + AI triage',
    disposition: 'under_systemic_oversight',
    lastAttested: T(-8),
    nextAttestationDue: T(82),
    riskTier: 'high',
    aiSystemsCount: 5,
    frameworkCoveragePct: 96,
    score: 94,
    signals: [
      { kind: 'sig_2026_ai_attestation', label: 'SIG 2026 AI section + HIPAA + AI', observed: T(-8) },
      { kind: 'soc2_ai_supplement', label: 'SOC 2 Type II + AI', observed: T(-60) },
      { kind: 'contract_ai_addendum', label: 'BAA with AI-specific representations', observed: T(-14) },
    ],
    namedSigner: { displayName: 'Anika Sharma', role: 'CISO', company: 'HaloDoc Health' },
    narrative: 'Five AI systems including patient triage assistant. HITRUST v11 in progress. HHS/OCR §1557 bias review completed 2026-08.',
  },
  {
    id: 'sup-007',
    name: 'Vector Vault',
    category: 'Password manager',
    disposition: 'awaiting_classification',
    lastAttested: null,
    nextAttestationDue: T(90),
    riskTier: 'low',
    aiSystemsCount: 0,
    frameworkCoveragePct: 0,
    score: 55,
    signals: [
      { kind: 'contract_ai_addendum', label: 'Contract renewed — attestation form issued', observed: T(-3) },
    ],
    namedSigner: null,
    narrative: 'New supplier onboarded 3 days ago. Baseline 90-day classification period active. Initial attestation form due 2026-12-14.',
  },
  {
    id: 'sup-008',
    name: 'Bellwether Analytics AI',
    category: 'Marketing analytics + LLM copy',
    disposition: 'outside_oversight_scope',
    lastAttested: null,
    nextAttestationDue: T(-3),
    riskTier: 'medium',
    aiSystemsCount: 2,
    frameworkCoveragePct: 0,
    score: 18,
    signals: [
      { kind: 'discovered_undisclosed', label: 'Identity-log correlation: 12 employees using Bellwether Copilot', observed: T(-1) },
      { kind: 'identity_log_correlation', label: 'Marketing seat expanded 3× without procurement review', observed: T(-1) },
    ],
    namedSigner: null,
    narrative: 'Signed as marketing analytics only. Marketing team activated the Bellwether Copilot AI feature Q3 2026 without renegotiating the contract. AI-specific representations missing.',
    undisclosedAi: true,
  },
  {
    id: 'sup-009',
    name: 'Steelwave Documents',
    category: 'Contract lifecycle',
    disposition: 'under_systemic_oversight',
    lastAttested: T(-45),
    nextAttestationDue: T(45),
    riskTier: 'medium',
    aiSystemsCount: 2,
    frameworkCoveragePct: 87,
    score: 81,
    signals: [
      { kind: 'sig_2026_ai_attestation', label: 'SIG 2026 AI section — complete', observed: T(-45) },
      { kind: 'iso42001_certificate', label: 'ISO 42001 — in audit', observed: T(-45) },
    ],
    namedSigner: { displayName: 'Reyna Iyer', role: 'VP TPRM', company: 'Steelwave' },
    narrative: 'Contract-clause AI drafting and redlining. Data-boundary: customer content not used for training.',
  },
  {
    id: 'sup-010',
    name: 'Cirrus Notify',
    category: 'Transactional messaging',
    disposition: 'pending_review',
    lastAttested: T(-72),
    nextAttestationDue: T(18),
    riskTier: 'low',
    aiSystemsCount: 1,
    frameworkCoveragePct: 74,
    score: 71,
    signals: [
      { kind: 'sig_2026_ai_attestation', label: 'SIG 2026 AI section — partial', observed: T(-72) },
    ],
    namedSigner: { displayName: 'Nadia Osei', role: 'Fractional CISO', company: 'Cirrus Notify' },
    narrative: 'One AI feature: send-time optimization. Partial attestation — data-boundary declaration not signed.',
  },
  {
    id: 'sup-011',
    name: 'Talon Legal Research',
    category: 'Legal research AI',
    disposition: 'awaiting_classification',
    lastAttested: null,
    nextAttestationDue: T(88),
    riskTier: 'medium',
    aiSystemsCount: 1,
    frameworkCoveragePct: 0,
    score: 58,
    signals: [
      { kind: 'contract_ai_addendum', label: 'Contract signed 2 days ago — attestation form pending', observed: T(-2) },
    ],
    namedSigner: null,
    narrative: 'New supplier. Baseline 90-day classification period active.',
  },
  {
    id: 'sup-012',
    name: 'Aetos Vision Systems',
    category: 'Computer vision + security',
    disposition: 'outside_oversight_scope',
    lastAttested: T(-410),
    nextAttestationDue: T(-320),
    riskTier: 'high',
    aiSystemsCount: 4,
    frameworkCoveragePct: 12,
    score: 8,
    signals: [
      { kind: 'sig_2026_ai_attestation', label: 'Attestation last completed 2025-Q2 — expired', observed: T(-410) },
      { kind: 'identity_log_correlation', label: 'Continues to process our video streams', observed: T(-1) },
    ],
    namedSigner: null,
    narrative: 'High-severity gap: expired attestation on active vendor. Board-escalation triggered. Renewal review scheduled 2026-09-25.',
  },
];

export const SUPPLIER_SUMMARY = {
  totalSuppliers: SUPPLIER_ROWS.length,
  underSystemicOversight: SUPPLIER_ROWS.filter((r) => r.disposition === 'under_systemic_oversight').length,
  pendingReview: SUPPLIER_ROWS.filter((r) => r.disposition === 'pending_review').length,
  awaitingClassification: SUPPLIER_ROWS.filter((r) => r.disposition === 'awaiting_classification').length,
  outsideOversightScope: SUPPLIER_ROWS.filter((r) => r.disposition === 'outside_oversight_scope').length,
  undisclosedAi: SUPPLIER_ROWS.filter((r) => r.undisclosedAi).length,
  onActiveRemediationSla: SUPPLIER_ROWS.filter(
    (r) => r.disposition === 'outside_oversight_scope' || r.disposition === 'pending_review',
  ).length,
  averageScore: Math.round(
    SUPPLIER_ROWS.reduce((sum, r) => sum + r.score, 0) / SUPPLIER_ROWS.length,
  ),
};

export const DISPOSITION_LABELS: Record<SupplierDisposition, string> = {
  under_systemic_oversight: 'Under systemic oversight',
  pending_review: 'Pending review',
  awaiting_classification: 'Awaiting classification',
  outside_oversight_scope: 'Outside oversight scope',
  retired: 'Retired',
};
